import { useSearchParams } from 'react-router-dom';
import SectionCard from '../../molecule/SectionCard';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '../../ui/form';
import {
  BaseFilterProps,
  ListFilterTypeObject,
  WithOptionsFilterFilters,
} from './types';
import { useMemo } from 'react';
import { ControllerRenderProps, useForm } from 'react-hook-form';
import { cn } from '@/src/utils';
import { filterListVariants } from './variants';
import _ from 'lodash';
import Select from '../../atom/Select';
import DateRangePicker from '../../atom/DateRangePicker';
import Input from '../../atom/Input';
import Button from '../../atom/Button';
import IconButton from '@/src/components/atom/Button/IconButton';
import { BUTTON_CONST } from '@/src/constants/buttons';

const DEFAULT_DATE_RANGE_PREFIX = 'started';

const renderFilter = (
  item: BaseFilterProps['filters'][number],
  field: ControllerRenderProps,
) => {
  const [from, to] = (field.value as string)?.split(',') || [];
  const { ref, ...fieldRest } = field;

  switch (item.type) {
    case ListFilterTypeObject.TEXT:
      return (
        <Input
          {...field}
          className="placeholder:text-gray4"
          containerClassName="flex-1"
          placeholder={item.placeholder}
          onClear={() => field.onChange('')}
          isEnterSubmit
        />
      );
    case ListFilterTypeObject.SELECT:
      return (
        <Select
          option={[
            {
              optionList: (item as WithOptionsFilterFilters).options,
            },
          ]}
          ref={ref}
          rootProps={{
            ...fieldRest,
            onValueChange: field.onChange,
          }}
          valueProps={{
            placeholder: item.placeholder,
          }}
        />
      );
    case ListFilterTypeObject.DATE_RANGE:
      return (
        <DateRangePicker
          {...field}
          value={
            from || to
              ? {
                  from: from ? new Date(from) : undefined,
                  to: to ? new Date(to) : undefined,
                }
              : undefined
          }
          onChange={value => {
            if (value === undefined) {
              field.onChange('');

              return;
            }

            field.onChange(
              `${value?.from}${value?.to ? `,${value?.to}` : ','}`,
            );
          }}
        />
      );
    case ListFilterTypeObject.PHONE:
      return (
        <Input
          {...field}
          className="placeholder:text-gray4"
          containerClassName="flex-1"
          placeholder={item.placeholder}
          onChange={e => field.onChange(e.target.value.replaceAll('-', ''))}
          onClear={() => field.onChange('')}
        />
      );
    default:
      return (
        <Input
          {...field}
          className="placeholder:text-gray4"
          containerClassName="flex-1"
          placeholder={item.placeholder}
          onClear={() => field.onChange('')}
          isEnterSubmit
        />
      );
  }
  ``;
};

export default function Filter(props: BaseFilterProps) {
  const { title, filters, cols = 3, onSubmit, hasQueryParams = true } = props;

  // util
  const [searchParams, setSearchParams] = useSearchParams();

  // state
  const defaultValues = useMemo(() => {
    const target = Object.fromEntries(searchParams);

    return hasQueryParams ? target : {};
  }, [hasQueryParams, searchParams]);

  // form
  const form = useForm({
    defaultValues: defaultValues,
  });

  // event
  const handleSubmit = () => {
    const target = _.cloneDeep(form.watch());

    const submitValues = Object.entries(target).reduce((acc, [key, value]) => {
      const filterItem = filters.find(item => item.key === key);

      if (filterItem?.type === ListFilterTypeObject.DATE_RANGE) {
        const [fromKey, toKey] = filterItem.rangeKeys || [];
        const [from, to] = value?.split(',') || [];

        return {
          ...acc,
          [fromKey || `${DEFAULT_DATE_RANGE_PREFIX}From`]: from || undefined,
          [toKey || `${DEFAULT_DATE_RANGE_PREFIX}To`]: to || undefined,
        };
      }

      return { ...acc, [key]: value };
    }, {});

    if (hasQueryParams) {
      setSearchParams(_.omitBy(target, _.isEmpty), { replace: true });
    }
    onSubmit(submitValues);
  };

  const handleReset = () => {
    setSearchParams({}, { replace: true });
    form.reset(_.mapValues(form.watch(), v => (_.isArray(v) ? [] : '')) as any);
    handleSubmit();
  };

  return (
    <SectionCard
      cardTitle={title}
      className="p-4"
      contentProps={{ className: 'p-0' }}
    >
      <Form {...form}>
        <form
          className="mt-2 flex flex-col gap-2"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <div className={cn(filterListVariants({ cols }))}>
            {filters.map(item => (
              <FormField
                key={item.key}
                control={form.control}
                name={item.key}
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormLabel style={{ width: '144px', flexShrink: 0 }}>
                      {item.label}
                    </FormLabel>
                    <FormControl className="flex-1">
                      {renderFilter(item, field)}
                    </FormControl>
                  </FormItem>
                )}
              />
            ))}
          </div>
          <div className="flex gap-2 self-end">
            <IconButton
              buttonSize="medium"
              buttonColor="outline"
              onClick={handleReset}
              lucideIconProps={{ name: 'RefreshCw' }}
            />
            <Button type="submit" buttonSize="medium" buttonColor="secondary">
              {BUTTON_CONST.search}
            </Button>
          </div>
        </form>
      </Form>
    </SectionCard>
  );
}
